/**
 * Seamless login when opening the safety app from Beacon Ops org-panel.
 *
 * Deep links use a URL **hash** fragment: #handoff=CODE (not a query param),
 * so the code is less likely to hit server logs / Referer. We exchange the
 * one-time code for a Firebase custom token and sign in — no second password.
 *
 * Security notes (client):
 * - Prefer #handoff= over ?handoff= (hash not sent on HTTP requests / Referer)
 * - Strip handoff from the address bar before the exchange fetch
 * - Never log the code or custom token
 * - Reject obviously malformed codes before calling the network
 */
import { signInWithCustomToken } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { orgHostBaseUrl } from '@/lib/orgPanel'

/** Matches beacon-ops AuthHandoffService (Str::random, length 40–64). */
const HANDOFF_CODE_RE = /^[A-Za-z0-9]{40,64}$/

const EXCHANGE_TIMEOUT_MS = 12_000

/** Read handoff from hash (#handoff=) or legacy query (?handoff=). */
function extractHandoffCode(): string | null {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  if (hash) {
    const hashParams = new URLSearchParams(hash)
    const fromHash = hashParams.get('handoff')
    if (fromHash) return fromHash
  }

  // Backward-compatible with older links that used ?handoff=
  const fromQuery = new URLSearchParams(window.location.search).get('handoff')
  return fromQuery
}

function stripHandoffFromUrl(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('handoff')

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash
  if (hash) {
    const hashParams = new URLSearchParams(hash)
    hashParams.delete('handoff')
    const remaining = hashParams.toString()
    url.hash = remaining ? `#${remaining}` : ''
  }

  const clean =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams}` : '') +
    url.hash
  window.history.replaceState({}, '', clean)
}

/**
 * If the URL has a handoff code, exchange it and sign into Firebase.
 * Strips handoff from the address bar ASAP.
 * Never logs the code or custom token.
 * Safe to call before authStore.init().
 */
export async function consumeAuthHandoff(): Promise<boolean> {
  const code = extractHandoffCode()
  if (!code) return false

  // Remove from the bar ASAP so refresh / share / history is less risky
  stripHandoffFromUrl()

  if (!HANDOFF_CODE_RE.test(code)) {
    console.warn('[authHandoff] exchange failed: malformed code')
    return false
  }

  // Embed opens a new iframe each tool switch, but Firebase auth is persisted.
  // If we already have a session, skip the handoff network round-trip.
  try {
    await auth.authStateReady()
    if (auth.currentUser) {
      return true
    }
  } catch {
    // Fall through to exchange
  }

  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS)

  try {
    const res = await fetch(`${orgHostBaseUrl()}/auth/handoff-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ handoff: code }),
      signal: controller.signal,
      // No cookies needed; code is the sole credential for this call
      credentials: 'omit',
      cache: 'no-store',
    })

    if (!res.ok) {
      // Status only — never log response body or the code
      console.warn('[authHandoff] exchange failed', res.status)
      return false
    }

    const data = (await res.json()) as { custom_token?: unknown }
    if (typeof data.custom_token !== 'string' || data.custom_token.length < 20) {
      console.warn('[authHandoff] exchange response incomplete')
      return false
    }

    await signInWithCustomToken(auth, data.custom_token)
    return true
  } catch {
    console.warn('[authHandoff] exchange error')
    return false
  } finally {
    window.clearTimeout(timer)
  }
}
